import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  BookOpen,
  Bookmark,
  Check,
  ExternalLink,
  Lightbulb,
  ListTodo,
  Loader2,
  MessageSquare,
  Mic,
  RefreshCw,
  Send,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { reportUiError } from '../../sentry';

type SourceKey = 'articles' | 'transcripts' | 'people' | 'references';

type ArticleRef = {
  kind: 'article';
  id: string;
  title: string;
  url: string;
  path: string;
  similarity: number;
};

type TranscriptRef = {
  kind: 'transcript';
  id: string;
  title: string;
  path: string;
  similarity: number;
};

type PersonRef = {
  kind: 'person';
  id: number;
  name: string;
  email: string | null;
  path: string;
  linkedin_url?: string | null;
  similarity: number;
};

type ReferenceRef = {
  kind: 'reference';
  id: string;
  title: string;
  url: string | null;
  usage?: string;
  path: string;
  similarity: number;
};

type CorpusRefs = {
  articles?: ArticleRef[];
  transcripts?: TranscriptRef[];
  people?: PersonRef[];
  references?: ReferenceRef[];
};

type ProposedAction = {
  id: string;
  type: 'save_idea' | 'create_task' | 'open_person' | 'open_transcript' | 'open_article';
  title: string;
  rationale?: string;
  seed_idea?: string;
  notes?: string;
  task_type?: string;
  due_days?: number;
  connection_id?: number;
  person_name?: string;
  person_email?: string | null;
  transcript_id?: string;
  url?: string | null;
  status?: 'proposed' | 'confirmed' | 'dismissed';
};

type ChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at?: string;
  retrieval?: {
    refs?: CorpusRefs;
    proposed_actions?: ProposedAction[];
  } | null;
};

const SUGGESTIONS = [
  'What is this person interested in based on their profile and my notes?',
  'Who should they talk to in my network — and why?',
  'What have I written or saved that I should share with them?',
  'Pull themes from my forwarded emails / saved references on AI overwhelm.',
];

const SOURCE_LABELS: { key: SourceKey; label: string; icon: React.ReactNode }[] = [
  { key: 'articles', label: 'Articles', icon: <BookOpen className="w-3.5 h-3.5" /> },
  { key: 'transcripts', label: 'Transcripts', icon: <Mic className="w-3.5 h-3.5" /> },
  { key: 'people', label: 'People', icon: <Users className="w-3.5 h-3.5" /> },
  { key: 'references', label: 'References', icon: <Bookmark className="w-3.5 h-3.5" /> },
];

type ScoredItem = {
  key: string;
  kind: SourceKey;
  title: string;
  subtitle?: string;
  score: number;
  path?: string;
  url?: string | null;
  linkedin_url?: string | null;
};

function simScore(raw: number | undefined | null): number {
  const n = typeof raw === 'number' && Number.isFinite(raw) ? raw : 0;
  // Cosine-ish similarity often ~0.2–0.6; map to 0–100 for display.
  return Math.round(Math.max(0, Math.min(1, n)) * 100);
}

function flattenRefs(refs: CorpusRefs | null | undefined): ScoredItem[] {
  if (!refs) return [];
  const items: ScoredItem[] = [];
  for (const p of refs.people ?? []) {
    items.push({
      key: `p-${p.id}`,
      kind: 'people',
      title: p.name,
      subtitle: p.email ?? undefined,
      score: simScore(p.similarity),
      path: p.path,
      linkedin_url: p.linkedin_url,
    });
  }
  for (const t of refs.transcripts ?? []) {
    items.push({
      key: `t-${t.id}`,
      kind: 'transcripts',
      title: t.title,
      score: simScore(t.similarity),
      path: t.path,
    });
  }
  for (const a of refs.articles ?? []) {
    items.push({
      key: `a-${a.id}`,
      kind: 'articles',
      title: a.title,
      score: simScore(a.similarity),
      url: a.url,
      path: a.path,
    });
  }
  for (const r of refs.references ?? []) {
    items.push({
      key: `r-${r.id}`,
      kind: 'references',
      title: r.title,
      subtitle: r.usage,
      score: simScore(r.similarity),
      url: r.url,
      path: r.path || '/references',
    });
  }
  return items.sort((a, b) => b.score - a.score);
}

const KIND_META: Record<
  SourceKey,
  { label: string; icon: React.ReactNode; className: string }
> = {
  people: {
    label: 'People',
    icon: <Users className="w-3.5 h-3.5" />,
    className:
      'bg-violet-100 dark:bg-violet-950/50 text-violet-800 dark:text-violet-200 border-violet-200 dark:border-violet-800',
  },
  transcripts: {
    label: 'Transcripts',
    icon: <Mic className="w-3.5 h-3.5" />,
    className:
      'bg-sky-100 dark:bg-sky-950/50 text-sky-800 dark:text-sky-200 border-sky-200 dark:border-sky-800',
  },
  articles: {
    label: 'Articles',
    icon: <BookOpen className="w-3.5 h-3.5" />,
    className:
      'bg-emerald-100 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-200 border-emerald-200 dark:border-emerald-800',
  },
  references: {
    label: 'References',
    icon: <Bookmark className="w-3.5 h-3.5" />,
    className:
      'bg-amber-100 dark:bg-amber-950/50 text-amber-900 dark:text-amber-200 border-amber-200 dark:border-amber-800',
  },
};

function RefSourceBadges({
  refs,
  onOpen,
}: {
  refs?: CorpusRefs | null;
  onOpen: (focus?: SourceKey) => void;
}) {
  const items = flattenRefs(refs);
  if (!items.length) return null;

  const counts: Partial<Record<SourceKey, { count: number; top: number }>> = {};
  for (const item of items) {
    const cur = counts[item.kind] ?? { count: 0, top: 0 };
    cur.count += 1;
    cur.top = Math.max(cur.top, item.score);
    counts[item.kind] = cur;
  }

  const topOverall = items[0];

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      {(Object.keys(KIND_META) as SourceKey[]).map((kind) => {
        const stats = counts[kind];
        if (!stats) return null;
        const meta = KIND_META[kind];
        return (
          <button
            key={kind}
            type="button"
            onClick={() => onOpen(kind)}
            className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${meta.className}`}
            title={`Top match ${stats.top}% — click to dig in`}
          >
            {meta.icon}
            <span>
              {meta.label} {stats.count}
            </span>
            <span className="tabular-nums opacity-70">{stats.top}%</span>
          </button>
        );
      })}
      <button
        type="button"
        onClick={() => onOpen()}
        className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
      >
        Dig deeper
        {topOverall ? (
          <span className="tabular-nums opacity-70">· best {topOverall.score}%</span>
        ) : null}
      </button>
    </div>
  );
}

function RefsDigModal({
  open,
  refs,
  focus,
  onClose,
}: {
  open: boolean;
  refs?: CorpusRefs | null;
  focus?: SourceKey | null;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<SourceKey | 'all'>('all');

  useEffect(() => {
    if (open) setTab(focus ?? 'all');
  }, [open, focus]);

  if (!open) return null;

  const all = flattenRefs(refs);
  const filtered = tab === 'all' ? all : all.filter((i) => i.kind === tab);
  const strong = filtered.filter((i) => i.score >= 25);
  const shown = (strong.length >= 3 ? strong : filtered).slice(0, 16);

  const openItem = (item: ScoredItem) => {
    if (item.url) {
      window.open(item.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (item.path) {
      onClose();
      navigate(item.path);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Close"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg max-h-[80vh] overflow-hidden rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">
              Retrieved matches
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Ranked by relevance score (vector similarity)
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5 px-4 py-2 border-b border-gray-100 dark:border-gray-800">
          <button
            type="button"
            onClick={() => setTab('all')}
            className={`text-xs px-2.5 py-1 rounded-full border ${
              tab === 'all'
                ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200'
                : 'border-gray-300 dark:border-gray-600 text-gray-500'
            }`}
          >
            All {all.length}
          </button>
          {(Object.keys(KIND_META) as SourceKey[]).map((kind) => {
            const count = all.filter((i) => i.kind === kind).length;
            if (!count) return null;
            return (
              <button
                key={kind}
                type="button"
                onClick={() => setTab(kind)}
                className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border ${
                  tab === kind
                    ? KIND_META[kind].className
                    : 'border-gray-300 dark:border-gray-600 text-gray-500'
                }`}
              >
                {KIND_META[kind].icon}
                {KIND_META[kind].label} {count}
              </button>
            );
          })}
        </div>

        <div className="overflow-y-auto flex-1 p-2">
          {shown.length === 0 ? (
            <p className="text-sm text-gray-500 p-3">No matches in this group.</p>
          ) : (
            <ul className="space-y-1">
              {shown.map((item) => (
                <li key={item.key}>
                  <button
                    type="button"
                    onClick={() => openItem(item)}
                    className="w-full text-left flex items-start gap-3 rounded-lg px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/80"
                  >
                    <span
                      className={`mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-md border ${KIND_META[item.kind].className}`}
                    >
                      {KIND_META[item.kind].icon}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {item.title}
                      </span>
                      {item.subtitle ? (
                        <span className="block text-xs text-gray-500 truncate">{item.subtitle}</span>
                      ) : null}
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                        {item.score}%
                      </span>
                      <span className="block text-[10px] uppercase tracking-wide text-gray-400">
                        match
                      </span>
                    </span>
                    {item.linkedin_url ? (
                      <a
                        href={item.linkedin_url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="shrink-0 p-1 text-[#0A66C2]"
                        title="LinkedIn"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    ) : (
                      <ExternalLink className="w-3.5 h-3.5 shrink-0 text-gray-400 mt-1" />
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function actionLabel(type: ProposedAction['type']): string {
  switch (type) {
    case 'save_idea':
      return 'Save idea';
    case 'create_task':
      return 'Create task';
    case 'open_person':
      return 'Open person';
    case 'open_transcript':
      return 'Open transcript';
    case 'open_article':
      return 'Open article';
    default:
      return 'Action';
  }
}

type ActionCardsProps = {
  messageId: string;
  actions: ProposedAction[];
  busyId: string | null;
  onConfirm: (messageId: string, action: ProposedAction) => void;
  onDismiss: (messageId: string, action: ProposedAction) => void;
};

const ActionCards: React.FC<ActionCardsProps> = ({
  messageId,
  actions,
  busyId,
  onConfirm,
  onDismiss,
}) => {
  const visible = actions.filter((a) => (a.status ?? 'proposed') !== 'dismissed');
  if (!visible.length) return null;

  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
        Confirm to act
      </p>
      {visible.map((a) => {
        const done = a.status === 'confirmed';
        const busy = busyId === `${messageId}:${a.id}`;
        return (
          <div
            key={a.id}
            className={`rounded-md border px-3 py-2 text-sm ${
              done
                ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900 dark:bg-emerald-950/30'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950/40'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                  {a.type === 'save_idea' ? (
                    <Lightbulb className="w-3.5 h-3.5" />
                  ) : a.type === 'create_task' ? (
                    <ListTodo className="w-3.5 h-3.5" />
                  ) : (
                    <ExternalLink className="w-3.5 h-3.5" />
                  )}
                  {actionLabel(a.type)}
                </div>
                <p className="font-medium text-gray-900 dark:text-gray-100 mt-0.5">{a.title}</p>
                {a.rationale ? (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{a.rationale}</p>
                ) : null}
              </div>
              {done ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300 shrink-0">
                  <Check className="w-3.5 h-3.5" />
                  Done
                </span>
              ) : (
                <div className="flex gap-1 shrink-0">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onConfirm(messageId, a)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    Confirm
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onDismiss(messageId, a)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-50"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const SiteWritingChatView: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [asking, setAsking] = useState(false);
  const [actionBusy, setActionBusy] = useState<string | null>(null);
  const [indexedCount, setIndexedCount] = useState<number | null>(null);
  const [digRefs, setDigRefs] = useState<CorpusRefs | null>(null);
  const [digFocus, setDigFocus] = useState<SourceKey | null>(null);
  const [sources, setSources] = useState<SourceKey[]>([
    'articles',
    'transcripts',
    'people',
    'references',
  ]);
  const bottomRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const [{ data, error }, countRes] = await Promise.all([
        supabase
          .from('site_content_chat_messages')
          .select('id, role, content, created_at, retrieval')
          .eq('user_id', user.id)
          .order('created_at', { ascending: true })
          .limit(200),
        supabase.from('site_content').select('id', { count: 'exact', head: true }),
      ]);
      if (error) throw error;
      setMessages((data as ChatMessage[]) ?? []);
      setIndexedCount(countRes.count ?? 0);
    } catch (e) {
      reportUiError('site_content_chat_messages', 'list', e);
      toast.error(e instanceof Error ? e.message : 'Could not load chat');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, asking]);

  const patchActionStatus = async (
    messageId: string,
    actionId: string,
    status: 'confirmed' | 'dismissed',
  ) => {
    const msg = messages.find((m) => m.id === messageId);
    if (!msg?.retrieval) return;
    const actions = (msg.retrieval.proposed_actions ?? []).map((a) =>
      a.id === actionId ? { ...a, status } : a,
    );
    const retrieval = { ...msg.retrieval, proposed_actions: actions };
    const { error } = await supabase
      .from('site_content_chat_messages')
      .update({ retrieval })
      .eq('id', messageId);
    if (error) throw error;
    setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, retrieval } : m)));
  };

  const confirmAction = async (messageId: string, action: ProposedAction) => {
    setActionBusy(`${messageId}:${action.id}`);
    try {
      if (action.type === 'open_person' && action.connection_id) {
        navigate(`/connections/${action.connection_id}`);
      } else if (action.type === 'open_transcript' && action.transcript_id) {
        navigate(`/transcripts/${action.transcript_id}`);
      } else if (action.type === 'open_article' && action.url) {
        window.open(action.url, '_blank', 'noopener,noreferrer');
      } else if (action.type === 'save_idea') {
        const { error } = await supabase.from('thoughts').insert({
          title: action.title,
          seed_idea: action.seed_idea || action.rationale || action.title,
          status: 'draft',
          reference: 'writing-chat',
          embedding_status: 'needs_embedding',
        });
        if (error) throw error;
        toast.success('Idea saved to Thoughts');
      } else if (action.type === 'create_task') {
        const email = (action.person_email ?? '').trim();
        if (!email) {
          throw new Error('Need a person email on this action to create a networking task');
        }
        let subscriberId: string | null = null;
        const { data: sub } = await supabase
          .from('subscribers')
          .select('id')
          .ilike('email', email)
          .maybeSingle();
        if (sub?.id) subscriberId = String(sub.id);

        const due = new Date();
        due.setDate(due.getDate() + (action.due_days ?? 3));
        const taskType = action.task_type || 'follow_up';
        const { error } = await supabase.from('tasks').insert({
          subscriber_id: subscriberId,
          contact_email: email,
          task_type: taskType,
          title: action.title,
          status: 'pending',
          due_at: due.toISOString(),
          source: 'manual',
          notes: action.notes || action.rationale || null,
          metadata: {
            origin: 'writing_chat',
            connection_id: action.connection_id ?? null,
            person_name: action.person_name ?? null,
          },
          sort_order: 0,
        });
        if (error) throw error;
        toast.success('Task created');
      } else {
        throw new Error('Cannot confirm this action — missing link or fields');
      }

      await patchActionStatus(messageId, action.id, 'confirmed');
    } catch (e) {
      reportUiError('writing_chat_actions', action.type, e);
      toast.error(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setActionBusy(null);
    }
  };

  const dismissAction = async (messageId: string, action: ProposedAction) => {
    try {
      await patchActionStatus(messageId, action.id, 'dismissed');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not dismiss');
    }
  };

  const saveReplyAsIdea = async (msg: ChatMessage) => {
    try {
      const { error } = await supabase.from('thoughts').insert({
        title: msg.content.slice(0, 80).replace(/\s+/g, ' ').trim() || 'Chat idea',
        seed_idea: msg.content,
        status: 'draft',
        reference: 'writing-chat',
        embedding_status: 'needs_embedding',
      });
      if (error) throw error;
      toast.success('Reply saved as Thought');
    } catch (e) {
      reportUiError('thoughts', 'create_from_chat', e);
      toast.error(e instanceof Error ? e.message : 'Could not save idea');
    }
  };

  const toggleSource = (key: SourceKey) => {
    setSources((prev) => {
      if (prev.includes(key)) {
        if (prev.length === 1) return prev;
        return prev.filter((k) => k !== key);
      }
      return [...prev, key];
    });
  };

  const clearChat = async () => {
    if (!user?.id) return;
    if (!confirm('Clear this corpus chat history?')) return;
    try {
      const { error } = await supabase
        .from('site_content_chat_messages')
        .delete()
        .eq('user_id', user.id);
      if (error) throw error;
      setMessages([]);
      toast.success('Chat cleared');
    } catch (e) {
      reportUiError('site_content_chat_messages', 'delete', e);
      toast.error(e instanceof Error ? e.message : 'Could not clear chat');
    }
  };

  const send = async (raw: string) => {
    if (!user?.id || asking) return;
    const p = raw.trim();
    if (!p) return;

    const prior = messages;
    const historyPayload = prior.map((m) => ({ role: m.role, content: m.content }));

    const { data: userRow, error: insertUserErr } = await supabase
      .from('site_content_chat_messages')
      .insert({
        user_id: user.id,
        role: 'user',
        content: p,
      })
      .select('id, role, content, created_at, retrieval')
      .single();

    if (insertUserErr || !userRow) {
      toast.error(insertUserErr?.message ?? 'Could not save message');
      return;
    }

    const savedUserId = userRow.id as string;
    setMessages([...prior, userRow as ChatMessage]);
    setPrompt('');
    setAsking(true);

    try {
      const { data, error } = await supabase.functions.invoke<{
        reply?: string;
        error?: string;
        refs?: CorpusRefs;
        proposed_actions?: ProposedAction[];
      }>('site-content-prompt', {
        body: { prompt: p, history: historyPayload, sources },
      });
      if (error) throw new Error(error.message || 'Request failed');
      if (data?.error) throw new Error(data.error);
      if (!data?.reply) throw new Error('No reply from assistant');

      const retrieval = {
        refs: data.refs ?? {},
        proposed_actions: data.proposed_actions ?? [],
      };

      const { data: asstRow, error: insertAsstErr } = await supabase
        .from('site_content_chat_messages')
        .insert({
          user_id: user.id,
          role: 'assistant',
          content: data.reply,
          retrieval,
        })
        .select('id, role, content, created_at, retrieval')
        .single();

      if (insertAsstErr || !asstRow) {
        throw new Error(insertAsstErr?.message ?? 'Could not save reply');
      }

      setMessages((prev) => [...prev, asstRow as ChatMessage]);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Something went wrong';
      toast.error(message);
      await supabase.from('site_content_chat_messages').delete().eq('id', savedUserId);
      setMessages(prior);
      setPrompt(p);
    } finally {
      setAsking(false);
    }
  };

  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');

  return (
    <div className="max-w-3xl mx-auto p-6 flex flex-col min-h-[calc(100vh-6rem)]">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-emerald-500" />
            Writing chat
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Corpus Q&A with confirm-to-act: save ideas, create tasks, open people — like MCP, inside Engine.
            {indexedCount != null ? (
              <span className="ml-1 tabular-nums">({indexedCount} articles indexed)</span>
            ) : null}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => void load()}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => void clearChat()}
            disabled={!messages.length}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-gray-300 dark:border-gray-600 disabled:opacity-40"
          >
            <Trash2 className="w-4 h-4" />
            Clear
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {SOURCE_LABELS.map(({ key, label, icon }) => {
          const on = sources.includes(key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggleSource(key)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border ${
                on
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-200'
                  : 'border-gray-300 dark:border-gray-600 text-gray-500'
              }`}
            >
              {icon}
              {label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto space-y-4 mb-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 min-h-[20rem]">
            {messages.length === 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Start a thread — try a multi-step path:
                </p>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => void send(s)}
                      className="text-left text-sm px-3 py-2 rounded-md border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-50 dark:hover:bg-emerald-950/40"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((m) => (
                <div
                  key={m.id}
                  className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className="max-w-[92%]">
                    <div
                      className={`rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                        m.role === 'user'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                      }`}
                    >
                      {m.content}
                    </div>
                    {m.role === 'assistant' ? (
                      <>
                        <RefSourceBadges
                          refs={m.retrieval?.refs ?? null}
                          onOpen={(focus) => {
                            setDigFocus(focus ?? null);
                            setDigRefs(m.retrieval?.refs ?? null);
                          }}
                        />
                        <ActionCards
                          messageId={m.id}
                          actions={m.retrieval?.proposed_actions ?? []}
                          busyId={actionBusy}
                          onConfirm={(id, a) => void confirmAction(id, a)}
                          onDismiss={(id, a) => void dismissAction(id, a)}
                        />
                        <button
                          type="button"
                          onClick={() => void saveReplyAsIdea(m)}
                          className="mt-2 inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-300 hover:underline"
                        >
                          <Lightbulb className="w-3.5 h-3.5" />
                          Save this reply as an idea
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              ))
            )}
            {asking ? (
              <div className="flex justify-start">
                <div className="inline-flex items-center gap-2 text-sm text-gray-500 px-3 py-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Searching corpora + drafting actions…
                </div>
              </div>
            ) : null}
            <div ref={bottomRef} />
          </div>

          {lastAssistant ? (
            <div className="mb-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void saveReplyAsIdea(lastAssistant)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md border border-gray-300 dark:border-gray-600"
              >
                <Lightbulb className="w-3.5 h-3.5" />
                Save last reply as idea
              </button>
            </div>
          ) : null}

          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void send(prompt);
            }}
          >
            <input
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={asking}
              placeholder="Ask — then confirm tasks / ideas in the reply cards"
              className="flex-1 px-3 py-2.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={asking || !prompt.trim()}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {asking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send
            </button>
          </form>
        </>
      )}

      <RefsDigModal
        open={Boolean(digRefs)}
        refs={digRefs}
        focus={digFocus}
        onClose={() => {
          setDigRefs(null);
          setDigFocus(null);
        }}
      />
    </div>
  );
};

export default SiteWritingChatView;
